import { useState, type ReactNode } from 'react';
import { api, formatBRL, type Product } from '../api.js';

const SHOP_TYPE_LABEL: Record<number, string> = { 1: 'Mall', 2: 'Preferida', 4: 'Preferida+' };
import { copyToClipboard } from '../clipboard.js';
import { CATEGORY_NAME_BY_ID } from '../categories.js';
import { viewUrl } from '../viewLink.js';

/**
 * Tabela comparativa de ofertas: nome à esquerda, métricas à direita.
 *
 * A ordenação é local (sobre o que já veio do banco) — é instantânea e não
 * gasta chamada da Shopee. A ordenação da BUSCA, que decide o que a Shopee
 * devolve, é o `sortType` no painel de filtros.
 */

export type SortKey =
  | 'sales'
  | 'salesWindow'
  | 'velocity'
  | 'commissionCents'
  | 'commissionRate'
  | 'sellerCommissionRate'
  | 'priceCents'
  | 'discount'
  | 'rating'
  | 'potential';

export type OfferKind = 'PRODUCT' | 'SHOP' | 'CAMPAIGN';

/**
 * Uma coluna: cabeçalho e célula na MESMA definição.
 *
 * Antes eram duas listas paralelas — cabeçalho num array, células escritas à
 * mão na ordem certa dentro do <tr>. Inserir coluna no meio desalinhava a
 * tabela inteira sem erro de compilação.
 *
 * `key` presente = coluna ordenável. Vigência e tipo de campanha não são
 * número; ordenar por elas não diria nada.
 */
type Coluna = {
  id: string;
  label: string;
  hint: string;
  width: number;
  key?: SortKey;
  cell: (o: Product, ctx: { best: (k: SortKey, v: number) => string }) => ReactNode;
};

const traco = <span className="muted">—</span>;

/** Fim de vigência que a Shopee usa para "sem prazo" (ano 2999). */
const SEM_PRAZO_APOS = new Date('2100-01-01').getTime();

function vigencia(o: Product): ReactNode {
  if (!o.offerEndsAt) return traco;
  const fim = new Date(o.offerEndsAt);
  // A Shopee manda 31/12/2999 quando a campanha não tem data de fim. Mostrar
  // isso literalmente na tela seria absurdo.
  if (fim.getTime() >= SEM_PRAZO_APOS) return <span className="muted">sem prazo</span>;
  const dias = Math.ceil((fim.getTime() - Date.now()) / 86_400_000);
  if (dias < 0) return <span className="badge err">encerrada</span>;
  if (dias <= 7) return <span className="badge warn">{dias}d</span>;
  return <span className="muted">{fim.toLocaleDateString('pt-BR')}</span>;
}

const COLUNAS: Record<string, Coluna> = {
  preco: {
    id: 'preco',
    label: 'Preço',
    hint: 'Preço mínimo do produto',
    width: 74,
    key: 'priceCents',
    cell: (o) => (o.priceCents > 0 ? formatBRL(o.priceCents) : traco),
  },
  desconto: {
    id: 'desconto',
    label: 'Desc.',
    hint: 'Desconto exibido na página do produto',
    width: 56,
    key: 'discount',
    cell: (o) =>
      o.priceDiscountRate ? (
        <span className="badge ok">-{o.priceDiscountRate}%</span>
      ) : (
        traco
      ),
  },
  comissao: {
    id: 'comissao',
    label: 'Comissão',
    hint: 'Quanto você ganha por venda, em reais',
    width: 80,
    key: 'commissionCents',
    cell: (o) =>
      o.commissionCents > 0 ? <strong>{formatBRL(o.commissionCents)}</strong> : traco,
  },
  taxa: {
    id: 'taxa',
    label: '%',
    hint: 'Percentual de comissão total',
    width: 54,
    key: 'commissionRate',
    cell: (o) => `${(o.commissionRate * 100).toFixed(1)}%`,
  },
  xtra: {
    id: 'xtra',
    label: 'Xtra',
    hint: 'Comissão Xtra: a parte paga pelo vendedor, além da comissão da Shopee',
    width: 56,
    key: 'sellerCommissionRate',
    cell: (o) =>
      o.sellerCommissionRate ? (
        <span className="badge ok">+{(o.sellerCommissionRate * 100).toFixed(1)}%</span>
      ) : (
        traco
      ),
  },
  vendas: {
    id: 'vendas',
    label: 'Vendas',
    hint: 'Total de vendas acumuladas do produto',
    width: 74,
    key: 'sales',
    cell: (o) => (o.sales > 0 ? o.sales.toLocaleString('pt-BR') : traco),
  },
  janela: {
    id: 'janela',
    label: 'Vendeu',
    hint: 'Vendas no período escolhido, medidas por nós entre duas buscas',
    width: 86,
    key: 'salesWindow',
    cell: (o) => {
      if (o.salesWindow === undefined) return traco;
      const dias = o.salesWindowDays ?? 0;
      return (
        <span title={`${dias} dia(s) de medição`}>
          <strong>{o.salesWindow.toLocaleString('pt-BR')}</strong>
          <span className="muted" style={{ fontSize: 11 }}>
            {' '}
            /{dias}d
          </span>
        </span>
      );
    },
  },
  velocidade: {
    id: 'velocidade',
    label: 'Δ vendas',
    hint: 'Vendas desde a busca anterior — velocidade real, calculada por nós',
    width: 78,
    key: 'velocity',
    cell: (o) => {
      const d = velocity(o);
      // Sem histórico ainda é diferente de zero vendas novas — não podemos
      // mostrar "0" para algo que nunca foi medido.
      if (d === null) {
        return (
          <span className="muted" title="Ainda sem busca anterior para comparar">
            —
          </span>
        );
      }
      return d > 0 ? (
        <span style={{ color: 'var(--gain)', fontWeight: 600 }}>
          +{d.toLocaleString('pt-BR')}
        </span>
      ) : (
        <span className="muted">0</span>
      );
    },
  },
  potencial: {
    id: 'potencial',
    label: 'Potencial',
    hint: 'Comissão × vendas: estimativa de quanto o produto já girou em comissão',
    width: 80,
    key: 'potential',
    cell: (o) => {
      const p = o.commissionCents * o.sales;
      return p > 0 ? <span className="muted">{formatBRL(p)}</span> : traco;
    },
  },
  nota: {
    id: 'nota',
    label: 'Nota',
    hint: 'Avaliação da oferta na Shopee',
    width: 54,
    key: 'rating',
    cell: (o) => (o.ratingStar !== null ? o.ratingStar.toFixed(1) : traco),
  },
  tipoLoja: {
    id: 'tipoLoja',
    label: 'Selo',
    hint: 'Selo da loja na Shopee: Mall, Preferida ou Preferida+',
    width: 96,
    cell: (o) => {
      const tipos = Array.isArray(o.shopType) ? o.shopType : [];
      const nomes = tipos.map((t) => SHOP_TYPE_LABEL[t]).filter(Boolean);
      return nomes.length ? <span className="badge accent">{nomes.join(' · ')}</span> : traco;
    },
  },
  orcamento: {
    id: 'orcamento',
    label: 'Orçamento',
    hint: 'Orçamento restante da loja para comissão — quando a Shopee informa',
    width: 92,
    cell: (o) => (o.remainingBudget ? o.remainingBudget.toLocaleString('pt-BR') : traco),
  },
  tipoCampanha: {
    id: 'tipoCampanha',
    label: 'Tipo',
    hint: 'Campanha de coleção (lista curada) ou de categoria inteira',
    width: 96,
    cell: (o) =>
      o.campaignOfferType === 1 ? (
        <span className="badge">coleção</span>
      ) : o.campaignOfferType === 2 ? (
        <span className="badge">categoria</span>
      ) : (
        traco
      ),
  },
  vigencia: {
    id: 'vigencia',
    label: 'Vigência',
    hint: 'Até quando a campanha vale',
    width: 96,
    cell: (o) => vigencia(o),
  },
};

/**
 * Colunas por tipo de oferta.
 *
 * Loja e campanha não têm preço, desconto nem vendas — mostrar essas colunas
 * enchia a tela de traços e escondia o que de fato distingue uma campanha da
 * outra (comissão, tipo e vigência).
 */
const POR_TIPO: Record<OfferKind, string[]> = {
  PRODUCT: [
    'preco',
    'desconto',
    'comissao',
    'taxa',
    'xtra',
    'vendas',
    'velocidade',
    'potencial',
    'nota',
  ],
  SHOP: ['taxa', 'xtra', 'nota', 'tipoLoja', 'orcamento'],
  CAMPAIGN: ['taxa', 'tipoCampanha', 'vigencia'],
};

export function sortValue(offer: Product, key: SortKey): number {
  switch (key) {
    case 'sales':
      return offer.sales;
    case 'salesWindow':
      // -1 para não-medido: cai abaixo do que vendeu zero, que é a verdade.
      return offer.salesWindow ?? -1;
    case 'velocity':
      return velocity(offer) ?? -1;
    case 'commissionCents':
      return offer.commissionCents;
    case 'commissionRate':
      return offer.commissionRate;
    case 'sellerCommissionRate':
      return offer.sellerCommissionRate ?? -1;
    case 'priceCents':
      return offer.priceCents;
    case 'discount':
      return offer.priceDiscountRate ?? -1;
    case 'rating':
      return offer.ratingStar ?? -1;
    case 'potential':
      return offer.commissionCents * offer.sales;
  }
}

/** Vendas desde a busca anterior. null quando ainda não há histórico. */
export function velocity(offer: Product): number | null {
  if (offer.salesPrevious === null) return null;
  return offer.sales - offer.salesPrevious;
}

function categoryLabel(offer: Product): string | null {
  const path = Array.isArray(offer.categoryPath) ? offer.categoryPath : [];
  // Mostra o nível mais específico que soubermos nomear.
  for (const id of [...path].reverse()) {
    const name = CATEGORY_NAME_BY_ID.get(id);
    if (name) return name;
  }
  return null;
}

export function OfferTable({
  items,
  sortKey,
  sortDesc,
  onSort,
  onSelect,
  onDiscard,
  onShopItems,
}: {
  items: Product[];
  sortKey: SortKey;
  sortDesc: boolean;
  onSort: (key: SortKey) => void;
  onSelect: (offer: Product) => void;
  onDiscard: (offer: Product) => void;
  /**
   * Só em linha de LOJA: buscar os itens daquela loja na Shopee.
   *
   * Uma linha de loja não é postável — não tem preço nem foto de produto. O que
   * se quer de uma loja é o catálogo dela, e é isto que este botão faz.
   */
  onShopItems?: (offer: Product) => void;
}) {
  // O conjunto de colunas segue o tipo em exibição. A lista vem homogênea (a
  // aba já filtra por tipo); o primeiro item decide, com produto como padrão.
  const tipo = (items[0]?.kind ?? 'PRODUCT') as OfferKind;
  // A coluna "Vendeu" só existe quando há medição de período na resposta —
  // mostrar um traço em toda linha só ocuparia largura.
  const temJanela = items.some((i) => i.salesWindow !== undefined);
  const colunas = POR_TIPO[tipo]
    .flatMap((id) => (id === 'vendas' && temJanela ? ['vendas', 'janela'] : [id]))
    .map((id) => COLUNAS[id]!);

  // Destaca o melhor valor de cada coluna: comparar 50 linhas de número é
  // cansativo, e o olho acha o máximo muito mais rápido com marcação.
  const maxima = new Map<SortKey, number>();
  for (const col of colunas) {
    if (col.key) {
      maxima.set(col.key, Math.max(...items.map((i) => sortValue(i, col.key!)), 0));
    }
  }

  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <table className="offer-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left', minWidth: 210 }}>Oferta</th>
            {colunas.map((col) => (
              <th key={col.id} style={{ width: col.width }} title={col.hint}>
                {col.key ? (
                  <button
                    type="button"
                    className="th-sort"
                    onClick={() => onSort(col.key!)}
                    aria-current={sortKey === col.key ? 'true' : undefined}
                  >
                    {col.label}
                    {sortKey === col.key && <span>{sortDesc ? ' ↓' : ' ↑'}</span>}
                  </button>
                ) : (
                  // Coluna não numérica: rótulo simples, sem promessa de
                  // ordenação que não existe.
                  <span className="th-plain">{col.label}</span>
                )}
              </th>
            ))}
            {/* Loja tem um botão a mais ("Ver itens") e precisa de mais espaço. */}
            <th
              style={
                tipo === 'SHOP'
                  ? { width: 314, minWidth: 314 }
                  : { width: 236, minWidth: 236 }
              }
            />
          </tr>
        </thead>
        <tbody>
          {items.map((offer) => {
            const cat = categoryLabel(offer);
            const best = (key: SortKey, value: number) =>
              value > 0 && value === maxima.get(key) ? 'best' : '';

            return (
              <tr key={offer.id}>
                <td>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {offer.imageUrl ? (
                      <img src={offer.imageUrl} alt="" className="offer-thumb" loading="lazy" />
                    ) : (
                      <div className="offer-thumb" />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div className="offer-name" title={offer.name}>
                        {offer.name}
                      </div>
                      <div className="muted" style={{ fontSize: 11.5 }}>
                        {cat && <span>{cat}</span>}
                        {cat && offer.shopName && <span> · </span>}
                        {offer.shopName && <span>{offer.shopName}</span>}
                      </div>
                    </div>
                  </div>
                </td>

                {colunas.map((col) => (
                  <td
                    key={col.id}
                    className={`num ${col.key ? best(col.key, sortValue(offer, col.key)) : ''}`}
                  >
                    {col.cell(offer, { best })}
                  </td>
                ))}

                <td>
                  <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                    <ViewButton offer={offer} />
                    <CopyAffiliateButton offer={offer} />
                    {tipo === 'SHOP' && onShopItems && (
                      <button
                        type="button"
                        style={{ padding: '5px 11px', fontSize: 12.5 }}
                        onClick={() => onShopItems(offer)}
                        title="Buscar na Shopee os produtos desta loja"
                      >
                        Ver itens
                      </button>
                    )}
                    <button
                      type="button"
                      className="pick"
                      style={{ padding: '5px 11px', fontSize: 12.5 }}
                      onClick={() => onSelect(offer)}
                    >
                      Selecionar
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => onDiscard(offer)}
                      title="Descartar"
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Abre a oferta na Shopee numa aba nova. `rel="noreferrer"` porque a aba
 * aberta não precisa (nem deve) ter referência de volta para o painel.
 */
/**
 * Copia o link de afiliado com o sub-ID `manual`.
 *
 * Não copia o `offerLink` que veio da busca: aquele link não carrega sub-ID
 * nenhum, então a venda entraria no relatório sem canal. Pedimos ao servidor um
 * link com sub-ID `manual` (gerado uma vez e cacheado), e assim o que você cola
 * à mão aparece separado de Instagram/TikTok/WhatsApp no Desempenho.
 */
export function CopyAffiliateButton({ offer }: { offer: Product }) {
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'err'>('idle');
  const [erro, setErro] = useState<string | null>(null);

  async function copy() {
    setState('busy');
    setErro(null);
    try {
      const { shortLink } = await api.affiliateLink(offer.id, 'manual');
      const copiado = await copyToClipboard(shortLink);
      if (!copiado) {
        // O link existe; só a cópia falhou. Mostrar o link é melhor que só errar.
        setErro(shortLink);
        setState('err');
        return;
      }
      setState('ok');
      setTimeout(() => setState('idle'), 2000);
    } catch (cause) {
      setErro(cause instanceof Error ? cause.message : 'Falha ao gerar o link');
      setState('err');
    }
  }

  if (state === 'err' && erro) {
    return (
      <input
        readOnly
        value={erro}
        onFocus={(e) => e.currentTarget.select()}
        title="Não conseguimos copiar automaticamente. Selecione e copie."
        style={{ width: 150, fontSize: 11.5, padding: '4px 7px' }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      disabled={state === 'busy'}
      title="Copiar link de afiliado (sub-ID manual, para rastrear onde você colou)"
      style={{ padding: '5px 11px', fontSize: 12.5, whiteSpace: 'nowrap' }}
      className={state === 'ok' ? 'copied' : ''}
    >
      {state === 'busy' ? (
        <span className="spinner" />
      ) : state === 'ok' ? (
        '✓ Copiado'
      ) : (
        '🔗 Link'
      )}
    </button>
  );
}

export function ViewButton({ offer }: { offer: Product }) {
  const url = viewUrl(offer);
  if (!url) {
    return (
      <button type="button" disabled title="Esta oferta foi buscada antes de guardarmos o link original">
        Ver
      </button>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="btn-link"
      title="Abrir na Shopee (link sem rastreio de afiliado)"
    >
      Ver ↗
    </a>
  );
}
